import { DialogActions, DialogContent, Grid, Stack } from "@mui/material";
import { MutationStatus } from "@tanstack/react-query";
import { Form as FormikForm, FormikProvider, useFormik } from "formik";
import { Save, User } from "lucide-react";
import { FC } from "react";
import * as yup from "yup";

import FormikDatePicker from "components/forms/FormikDatePicker";
import FormikTextField from "components/forms/FormikTextField";
import MaskSocialInput from "components/forms/FormikTextField/MaskSocialInput";
import FormSection from "components/forms/FormSection";
import UltimateButton from "components/UltimateButton";

export type UpdateGeneralInfoFormValues = {
  firstName: string;
  lastName: string;
  middleName?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  social?: string;
  email: string;
  birthday: Date | null;
};

interface IUpdateGeneralInfoForm {
  handleSubmit: (values: UpdateGeneralInfoFormValues) => void;
  handleClose: () => void;
  initialValues?: UpdateGeneralInfoFormValues;
  submitStatus?: MutationStatus;
}

const UpdateGeneralInfoForm: FC<IUpdateGeneralInfoForm> = ({
  handleClose,
  handleSubmit,
  initialValues = {
    firstName: "",
    lastName: "",
    middleName: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    social: "",
    email: "",
    birthday: null,
  },
  submitStatus,
}) => {
  const schema = yup.object({
    firstName: yup.string().required().nullable(),
    lastName: yup.string().required().nullable(),
    address: yup.string().required().nullable(),
    city: yup.string().required().nullable(),
    state: yup.string().required().nullable(),
    zip: yup.string().required().nullable(),
    email: yup.string().required().nullable(),
  });

  const formik = useFormik({
    // enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: handleSubmit,
  });

  return (
    <FormikProvider value={formik}>
      <FormikForm>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <FormSection
              title="General Information"
              description="Name, address, and contact details."
              Icon={User}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} lg={4}>
                  <FormikTextField name="firstName" label="First name" />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <FormikTextField name="middleName" label="Middle name" />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <FormikTextField name="lastName" label="Last name" />
                </Grid>
                <Grid item xs={12}>
                  <FormikTextField name="address" label="Address" />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <FormikTextField name="city" label="City" />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <FormikTextField name="state" label="State" />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <FormikTextField name="zip" label="ZIP" />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikTextField name="phone" label="Phone" />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikTextField name="email" label="Email" />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikTextField
                    name="social"
                    label="Social"
                    InputProps={{
                      inputComponent: MaskSocialInput as any,
                    }}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikDatePicker name="birthday" label="Birthday" />
                </Grid>
              </Grid>
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions>
          <UltimateButton
            type="submit"
            text="Save & Refetch"
            Icon={
              Save as React.ComponentType<{
                size?: number;
                className?: string;
                style?: React.CSSProperties;
              }>
            }
            loading={submitStatus === "loading"}
          />
        </DialogActions>
      </FormikForm>
    </FormikProvider>
  );
};

export default UpdateGeneralInfoForm;
